import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { LOGO_TRANSPARENT } from "@/lib/logoData";
import HostUsageCard from "@/components/HostUsageCard";
import { Users, GraduationCap, BookCheck, Video, Award, Activity, Building2, Pencil, Save, Target } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";

const STATUS_COLORS = {
  "Excelente": "#3FA84D",
  "Avanzando": "#FFCC28",
  "En desarrollo": "#315197",
  "Requiere apoyo": "#D63384",
  "Pendiente diagnóstico": "#6E7591",
};

const LOGO = LOGO_TRANSPARENT; // v5.7.5 · logo HD

// [clave, etiqueta, unidad]
const GOAL_FIELDS = [
  ["franchises", "Franquicias", ""],
  ["students", "Estudiantes", ""],
  ["teachers", "Docentes", ""],
  ["diagnosed", "Diagnosticados", ""],
  ["classes_done", "Clases impartidas", ""],
  ["classes_upcoming", "Próximas clases", ""],
  ["certified_pct", "Tasa de certificación", "%"],
  ["certified", "Docentes certificados", ""],
];

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  // v5.7.5 · metas y notas ejecutivas (las edita solo el Súper Administrador)
  const [sn, setSn] = useState({ goals: {}, notes: "", can_edit: false });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ goals: {}, notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data)).catch(() => {});
    api.get("/dashboard/admin/summary-notes").then((r) => setSn(r.data)).catch(() => {});
  }, []);

  const startEdit = () => {
    const g = {};
    GOAL_FIELDS.forEach(([key]) => { g[key] = sn.goals?.[key] ?? ""; });
    setDraft({ goals: g, notes: sn.notes || "" });
    setEditing(true);
  };
  const saveSummary = async () => {
    setSaving(true);
    try {
      const goals = {};
      GOAL_FIELDS.forEach(([key]) => {
        const v = draft.goals[key];
        if (v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v))) goals[key] = Number(v);
      });
      const { data: saved } = await api.put("/dashboard/admin/summary-notes", { goals, notes: draft.notes });
      setSn(saved);
      setEditing(false);
      toast.success("Resumen ejecutivo guardado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-[#6E7591] font-medium">Cargando…</p>;

  const k = data.kpis;
  const certPct = k.teachers ? Math.round((k.certified_teachers / k.teachers) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      {/* Cabecera ejecutiva corporativa */}
      <div className="rounded-lg bg-[#1F2547] text-white overflow-hidden">
        <div className="px-6 py-5 sm:px-8 sm:py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-14 w-36 sm:w-44 rounded-lg bg-white flex items-center justify-center shrink-0 px-3 py-1.5">
              <img src={LOGO} alt="NEUROMATEMÁTICAS JASQ®" className="w-full h-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFCC28]">Panel administrativo</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Operación de la plataforma</h1>
              <p className="text-xs text-white/70 mt-0.5">Visión ejecutiva · NEUROMATEMÁTICAS JASQ®</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-franchises" label="Franquicias" value={k.franchises || 0} num={k.franchises || 0} goal={sn.goals?.franchises} icon={Building2} color="#1F2547" />
        <Kpi testid="kpi-students" label="Estudiantes" value={k.students} num={k.students} goal={sn.goals?.students} icon={Users} color="#315197" />
        <Kpi testid="kpi-teachers" label="Docentes" value={k.teachers} num={k.teachers} goal={sn.goals?.teachers} icon={GraduationCap} color="#A43790" />
        <Kpi testid="kpi-diagnosed" label="Diagnosticados" value={`${k.diagnosed}/${k.students}`} num={k.diagnosed} goal={sn.goals?.diagnosed} icon={BookCheck} color="#3FA84D" />
        <Kpi testid="kpi-classes-done" label="Clases impartidas" value={k.classes_done} num={k.classes_done} goal={sn.goals?.classes_done} icon={Activity} color="#1F2547" />
        <Kpi testid="kpi-classes-upcoming" label="Próximas" value={k.classes_upcoming} num={k.classes_upcoming} goal={sn.goals?.classes_upcoming} icon={Video} color="#315197" />
        <Kpi testid="kpi-certified" label="Tasa certificación" value={certPct + "%"} num={certPct} goal={sn.goals?.certified_pct} unit="%" icon={Award} color="#3FA84D" />
        <Kpi testid="kpi-certified-count" label="Certificados" value={k.certified_teachers} num={k.certified_teachers} goal={sn.goals?.certified} icon={Award} color="#D63384" />
      </div>

      {/* v5.7.5 · Metas y notas ejecutivas */}
      <div className="bg-white border border-[#E1E5F0] rounded-lg p-6" data-testid="exec-notes">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591]">Dirección</p>
            <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2547] mt-0.5 inline-flex items-center gap-2"><Target size={16} /> Metas y notas ejecutivas</h3>
            {sn.updated_at && (
              <p className="text-xs text-[#6E7591] mt-0.5">Última edición: {new Date(sn.updated_at).toLocaleString("es-MX")}{sn.updated_by ? ` · ${sn.updated_by}` : ""}</p>
            )}
          </div>
          {sn.can_edit && !editing && (
            <button onClick={startEdit} data-testid="exec-edit"
              className="inline-flex items-center gap-2 border border-[#E1E5F0] text-[#1F2547] text-sm font-semibold px-3 py-2 rounded-md hover:bg-[#F7F8FA]">
              <Pencil size={14} /> Editar
            </button>
          )}
        </div>

        {editing ? (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {GOAL_FIELDS.map(([key, label, unit]) => (
                <label key={key} className="block">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">Meta · {label}{unit ? ` (${unit})` : ""}</span>
                  <input type="number" min="0" max={key === "certified_pct" ? 100 : undefined} step="any"
                    value={draft.goals[key]} data-testid={`exec-goal-${key}`}
                    onChange={(e) => setDraft((d) => ({ ...d, goals: { ...d.goals, [key]: e.target.value } }))}
                    className="mt-1 w-full px-3 py-2 rounded-md border border-[#E1E5F0] focus:border-[#1F2547] outline-none text-sm" placeholder="Sin meta" />
                </label>
              ))}
            </div>
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#6E7591]">Notas ejecutivas</span>
              <textarea rows={5} maxLength={4000} value={draft.notes} data-testid="exec-notes-input"
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                className="mt-1 w-full px-3 py-2 rounded-md border border-[#E1E5F0] focus:border-[#1F2547] outline-none text-sm"
                placeholder="Prioridades, acuerdos, observaciones del periodo…" />
            </label>
            <div className="flex items-center gap-3">
              <button onClick={() => setEditing(false)} className="border border-[#E1E5F0] text-[#1F2547] text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#F7F8FA]">Cancelar</button>
              <button onClick={saveSummary} disabled={saving} data-testid="exec-save"
                className="inline-flex items-center gap-2 bg-[#1F2547] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#161A33] disabled:opacity-60">
                <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-[#1F2547] whitespace-pre-wrap" data-testid="exec-notes-text">
            {sn.notes || <span className="text-[#6E7591]">Sin notas todavía.{sn.can_edit ? " Usa «Editar» para fijar metas y escribir notas." : ""}</span>}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E1E5F0] rounded-lg p-6 lg:col-span-2" data-testid="distribution-chart">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591]">Analítica</p>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2547] mt-0.5">Distribución cualitativa</h3>
          <p className="text-sm text-[#6E7591] mb-5">Sin notas; clasificación por crecimiento integral.</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.distribution} barSize={42}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E1E5F0" vertical={false} />
                <XAxis dataKey="status" tick={{ fill: "#6E7591", fontSize: 11, fontWeight: 600 }} axisLine={{ stroke: "#E1E5F0" }} tickLine={false} />
                <YAxis tick={{ fill: "#6E7591", fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #E1E5F0", borderRadius: 8, fontSize: 12, fontWeight: 600 }} cursor={{ fill: "#F7F8FA" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {data.distribution.map((d, i) => (
                    <Cell key={i} fill={STATUS_COLORS[d.status] || "#315197"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-[#E1E5F0] rounded-lg p-6" data-testid="recent-activity">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#6E7591]">Registro</p>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#1F2547] mt-0.5 mb-4">Actividad reciente</h3>
          <ul className="space-y-4">
            {data.recent_activity.map((a, i) => (
              <li key={i} className="flex gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-[#A43790] mt-2 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1F2547]">
                    <span className="font-semibold">{a.who}</span> — {a.what}
                  </p>
                  <p className="text-xs text-[#6E7591] mt-0.5">{a.when}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <HostUsageCard />
    </div>
  );
}

function Kpi({ testid, label, value, num, goal, unit = "", icon: Icon, color }) {
  const hasGoal = goal !== undefined && goal !== null && goal !== "" && Number(goal) > 0;
  const pct = hasGoal ? Math.min(100, Math.round((Number(num || 0) / Number(goal)) * 100)) : 0;
  return (
    <div className="bg-white border border-[#E1E5F0] rounded-lg p-4 border-t-2 transition-all hover:shadow-sm hover:-translate-y-px"
      style={{ borderTopColor: color }} data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${color}14`, color }}>
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] tracking-wider uppercase font-semibold text-[#6E7591] truncate">{label}</p>
          <p className="font-display text-2xl font-bold tracking-tight text-[#1F2547] truncate">{value}</p>
        </div>
      </div>
      {hasGoal && (
        <div className="mt-3" data-testid={`${testid}-goal`}>
          <div className="h-1.5 rounded-full bg-[#E1E5F0] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
          <p className="text-[11px] text-[#6E7591] mt-1 font-semibold">Meta {goal}{unit} · {pct}%</p>
        </div>
      )}
    </div>
  );
}
